import { useEffect, useRef, useState } from 'react';
import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { spacing } from '../../theming';
import { useVinExtractionService } from '../../services/vinExtractionService';
import {
    CARS_PAGE_SIZE,
    CAR_TABS,
    SEARCH_DEBOUNCE_MS,
    errorTags,
    finalAttempt,
    parseBatchCars,
    searchParam,
} from '../../utils/vinExtraction';
import { cardTitleStyle, insetCardStyle, monoTextStyle, visuallyHiddenStyle } from './vinExtractionStyles';
import ErrorCodeTags from './ErrorCodeTags';
import NoValue from './NoValue';
import PreviousVinNote from './PreviousVinNote';
import { useVinRequest } from './useVinRequest';
import { useFailedCarNav } from './useFailedCarNav';
import CarDrawer from './CarDrawer';
import CarConfidence from './CarConfidence';
import CarStockCell from './CarStockCell';
import CarStatusCell from './CarStatusCell';
import CarDetailsButton from './CarDetailsButton';
import BatchCarsBody from './BatchCarsBody';
import BatchCarsToolbar from './BatchCarsToolbar';

const useColumns = onDetails => {
    const { t } = useTranslation('vinExtraction');

    return [
        { key: 'stockNo', title: t('cars.columns.stockNo'), render: (_, car) => <CarStockCell car={car} /> },
        {
            key: 'vin',
            title: t('cars.columns.vin'),
            render: (_, car) => (
                <Flex vertical align="flex-start" gap={spacing[0.5]}>
                    {car.vin ? <span style={monoTextStyle}>{car.vin}</span> : <NoValue label={t('cars.noVin')} />}
                    <PreviousVinNote car={car} />
                </Flex>
            ),
        },
        { key: 'status', title: t('cars.columns.status'), render: (_, car) => <CarStatusCell car={car} /> },
        {
            key: 'confidence',
            title: t('cars.columns.confidence'),
            render: (_, car) => <CarConfidence attempt={finalAttempt(car.attempts)} />,
        },
        {
            key: 'errors',
            title: t('cars.columns.errors'),
            render: (_, car) => <ErrorCodeTags tags={errorTags(finalAttempt(car.attempts))} />,
        },
        {
            key: 'actions',
            title: <span style={visuallyHiddenStyle}>{t('cars.columns.actions')}</span>,
            render: (_, car, index) => <CarDetailsButton car={car} index={index} onDetails={onDetails} />,
        },
    ];
};

/** GET /batches/:id/cars for the tab, search and page; the last answer stays on screen while the next one loads. */
const useCarsQuery = (batchId, refreshToken) => {
    const { getBatchCars } = useVinExtractionService();
    const [query, setQuery] = useState({ tab: 'all', search: undefined, page: 1, pageSize: CARS_PAGE_SIZE });
    const { load, run } = useVinRequest({
        request: () => {
            const status = CAR_TABS.find(tab => tab.key === query.tab).status;
            return getBatchCars(batchId, { status, search: query.search, page: query.page, pageSize: query.pageSize });
        },
        parse: parseBatchCars,
        keepData: true,
    });

    useEffect(() => {
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId, refreshToken, query]);

    return { query, setQuery, load, fetchCars: run };
};

/** Only a settled 2–32 character search reaches the API; shorter text means no search at all. */
const useDebouncedSearch = (searchText, setQuery) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            const search = searchParam(searchText);
            setQuery(prev => (prev.search === search ? prev : { ...prev, search, page: 1 }));
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [searchText, setQuery]);
};

/**
 * The car drawer, opened from a row's Details button. From the Failed tab it also starts previous / next through
 * the failed cars (E22). On close, focus goes back to the button that opened it.
 */
const useCarDrawer = ({ batchId, query, load }) => {
    const [drawer, setDrawer] = useState(null);
    const opener = useRef(null);
    const nav = useFailedCarNav({ batchId, search: query.search });

    const openDrawer = (car, index, trigger) => {
        opener.current = trigger;
        const { items, total, batch } = load.data;
        if (query.tab === 'failed') nav.start({ items, page: query.page, pageSize: query.pageSize, total, index });
        else nav.stop();
        setDrawer({ car, batch });
    };

    const closeDrawer = () => {
        setDrawer(null);
        nav.stop();
        // the drawer traps focus while open; give it back to the Details button that opened it
        setTimeout(() => opener.current?.focus(), 0);
    };

    const car = nav.active ? nav.car : drawer?.car;

    return { open: Boolean(drawer && car), car, batch: drawer?.batch, nav, openDrawer, closeDrawer };
};

/** A batch's cars from GET /batches/:id/cars: status tabs, debounced search, and the car drawer. */
export default function BatchCarsPanel({ batchId, refreshToken, currentThreshold }) {
    const { t } = useTranslation('vinExtraction');
    const cars = useCarsQuery(batchId, refreshToken);
    const { query, setQuery, load } = cars;
    const [searchText, setSearchText] = useState('');
    const drawer = useCarDrawer({ batchId, query, load });
    useDebouncedSearch(searchText, setQuery);
    const columns = useColumns(drawer.openDrawer);

    return (
        <Flex
            vertical
            gap={spacing[2]}
            style={insetCardStyle}
            role="region"
            aria-label={t('cars.aria', { id: batchId })}
            aria-busy={load.status === 'loading'}
        >
            <h3 style={{ ...cardTitleStyle, margin: 0 }}>{t('cars.title')}</h3>
            <BatchCarsToolbar
                counts={load.data?.counts}
                tab={query.tab}
                onTab={tab => setQuery(prev => ({ ...prev, tab, page: 1 }))}
                searchText={searchText}
                onSearch={setSearchText}
            />
            <BatchCarsBody cars={cars} columns={columns} />
            <CarDrawer
                open={drawer.open}
                onClose={drawer.closeDrawer}
                car={drawer.car}
                batch={drawer.batch}
                currentThreshold={currentThreshold}
                failedNav={drawer.nav.active ? drawer.nav : null}
            />
        </Flex>
    );
}
